import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Table, Modal, Form, Spinner, Row, Col, Tooltip, OverlayTrigger, Alert } from 'react-bootstrap';
import axios from 'axios';
import { useAlertModal } from '../../AlertModalContext';
import { useUserContext } from '../../UserContext'; // Importar o hook para acessar dynamicAccessConfig

const PerfisTab = ({ currentEntidade }) => {
    const [perfis, setPerfis] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentPerfil, setCurrentPerfil] = useState({
        id: null,
        nome: '',
        descricao: '',
        tipo: 'customizado', // 'padrao' ou 'customizado'
        entidade_id: null,
        permissoes: { // Estrutura esperada para perfis customizados vindos da API
            modulos: [], // ex: ['consultorio', 'financeiro']
            abas: {
                // consultorio: ['agenda', 'pacientes'],
                // financeiro: ['resumoGeral']
            }
        }
    });
    const { showAlertModal } = useAlertModal();
    const fetchedPerfisForEntityId = useRef(null); // Ref para controlar a busca de dados

    const fetchPerfis = useCallback(async () => {
        // Usa o ID da entidade para a verificação, que é mais estável que o objeto
        if (!currentEntidade?.id) return;

        // Se já buscamos os perfis para esta entidade, não fazemos a chamada novamente.
        // Isso previne chamadas duplicadas causadas pelo StrictMode do React em desenvolvimento.
        if (fetchedPerfisForEntityId.current === currentEntidade.id) return;

        setIsLoading(true);
        try {
            // API para buscar perfis (padrão + customizados da entidade)
            // Ex: /api/perfis?entidade_id=${currentEntidade.id}
            // A API deve retornar os perfis customizados com um campo 'permissoes'
            const response = await axios.get(`/api/perfis?entidade_id=${currentEntidade.id}`); // Definir response
            setPerfis(response.data.perfis || []);
            // Marca que a busca para esta entidade foi concluída com sucesso.
            fetchedPerfisForEntityId.current = currentEntidade.id;
        } catch (error) {
            console.error("Erro ao buscar perfis:", error);
            showAlertModal('Erro', 'Não foi possível carregar os perfis.', 'danger');
            setPerfis([ // Fallback com perfis padrão em caso de erro na API
                { id: 'std1', nome: 'Representante Legal', descricao: 'Acesso total ao sistema e configurações da entidade.', tipo: 'padrao', entidade_id: null },
                { id: 'std2', nome: 'Médico', descricao: 'Acesso às funcionalidades do consultório e prontuários.', tipo: 'padrao', entidade_id: null },
                { id: 'std3', nome: 'Recepção', descricao: 'Acesso à agenda, cadastro de pacientes e financeiro básico.', tipo: 'padrao', entidade_id: null },
            ]);
        } finally {
            setIsLoading(false);
        }
    }, [currentEntidade?.id, showAlertModal]); // Depende do ID da entidade, que é um valor primitivo e mais estável.

    const { dynamicAccessConfig, isLoadingAccessConfig } = useUserContext(); // Obter a config da API

    useEffect(() => {
        fetchPerfis();
    }, [fetchPerfis]); // Removido currentEntidade daqui, pois fetchPerfis já depende dele

    const handleOpenModal = (perfil = null) => {
        if (perfil) {
            if (perfil.tipo === 'padrao') {
                showAlertModal('Atenção', 'Perfis padrão não podem ser editados.', 'warning');
                return;
            }
            setIsEditing(true);
            // Garante que 'permissoes' seja sempre um objeto válido para edição
            const perfilComPermissoes = {
                ...perfil,
                permissoes: perfil.permissoes || { modulos: [], abas: {} }
            };
            setCurrentPerfil({ ...perfilComPermissoes, entidade_id: currentEntidade?.id });
        } else {
            setIsEditing(false);
            setCurrentPerfil({
                id: null,
                nome: '',
                descricao: '',
                tipo: 'customizado',
                entidade_id: currentEntidade?.id,
                permissoes: { modulos: [], abas: {} } // Inicializa permissões para novo perfil
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentPerfil({
            id: null, nome: '', descricao: '', tipo: 'customizado', entidade_id: null,
            permissoes: {
                modulos: [],
                abas: {}
            }
        });
    };

    const handleChange = (e) => {
        setCurrentPerfil({ ...currentPerfil, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentPerfil.nome.trim()) {
            showAlertModal('Validação', 'O nome do perfil é obrigatório.', 'warning');
            return;
        }
        if (!currentEntidade) {
            showAlertModal('Erro', 'Nenhuma entidade selecionada.', 'danger');
            return;
        }

        setIsLoading(true);
        const payload = { ...currentPerfil, entidade_id: currentEntidade.id };

        try {
            if (isEditing) {
                await axios.put(`/api/perfis/${currentPerfil.id}`, payload);
                showAlertModal('Sucesso', 'Perfil atualizado com sucesso!', 'success');
            } else {
                await axios.post('/api/perfis', payload);
                showAlertModal('Sucesso', 'Perfil criado com sucesso!', 'success');
            }
            fetchPerfis();
            handleCloseModal();
        } catch (error) {
            console.error("Erro ao salvar perfil:", error);
            showAlertModal('Erro', `Não foi possível salvar o perfil: ${error.response?.data?.message || error.message}`, 'danger');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (perfilId, tipoPerfil) => {
        if (tipoPerfil === 'padrao') {
            showAlertModal('Atenção', 'Perfis padrão não podem ser excluídos.', 'warning');
            return;
        }
        if (window.confirm('Tem certeza que deseja excluir este perfil? Esta ação não poderá ser desfeita.')) {
            setIsLoading(true);
            try {
                await axios.delete(`/api/perfis/${perfilId}`);
                showAlertModal('Sucesso', 'Perfil excluído com sucesso!', 'success');
                fetchPerfis();
            } catch (error) {
                console.error("Erro ao excluir perfil:", error);
                showAlertModal('Erro', `Não foi possível excluir o perfil: ${error.response?.data?.message || error.message}`, 'danger');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handlePermissaoModuloChange = (moduleKey, isChecked) => {
        setCurrentPerfil(prev => {
            const newModulos = isChecked
                ? [...(prev.permissoes.modulos || []), moduleKey]
                : (prev.permissoes.modulos || []).filter(mKey => mKey !== moduleKey);

            // Se desmarcar o módulo, também desmarca todas as suas abas
            const newAbas = { ...(prev.permissoes.abas || {}) };
            if (!isChecked) {
                delete newAbas[moduleKey];
            }

            return { ...prev, permissoes: { ...prev.permissoes, modulos: newModulos, abas: newAbas } };
        });
    };

    const handlePermissaoAbaChange = (moduleKey, tabKey, isChecked) => {
        setCurrentPerfil(prev => {
            const abasDoModulo = prev.permissoes.abas?.[moduleKey] || [];
            const newAbasDoModulo = isChecked
                ? [...abasDoModulo, tabKey]
                : abasDoModulo.filter(tKey => tKey !== tabKey);

            const newPermissoesAbas = {
                ...(prev.permissoes.abas || {}),
                [moduleKey]: newAbasDoModulo,
            };
            // Se todas as abas de um módulo forem desmarcadas, remove a entrada do módulo em 'abas'
            if (newAbasDoModulo.length === 0) {
                delete newPermissoesAbas[moduleKey];
            }
            return { ...prev, permissoes: { ...prev.permissoes, abas: newPermissoesAbas } };
        });
    };

    // Estrutura todas as permissões possíveis (Módulos e Abas internas)
    const structuredPermissions = useMemo(() => {
        // Constrói a partir do objeto dynamicAccessConfig vindo do UserContext
        return Object.entries(dynamicAccessConfig)
            .map(([moduleKey, moduleConfig]) => {
                if (!moduleConfig || !moduleConfig.title) return null;

                const internalTabs = Object.entries(moduleConfig)
                    .map(([itemKey, itemConfig]) => {
                        // Verifica se é uma aba (tem 'key', 'title') e não é o 'header', 'key' ou 'title' do módulo principal
                        if (itemKey !== 'header' && itemKey !== 'key' && itemKey !== 'title' &&
                            itemConfig && typeof itemConfig === 'object' && itemConfig.key && itemConfig.title) {
                               return { tabKey: itemConfig.key, tabTitle: itemConfig.title };
                        }
                        return null;
                    })
                    .filter(Boolean);

                return {
                    moduleKey: moduleConfig.key || moduleKey,
                    moduleTitle: moduleConfig.title,
                    tabs: internalTabs,
                };
            })
            .filter(Boolean);
    }, [dynamicAccessConfig]); // Agora depende de dynamicAccessConfig

// Adiciona verificação para isLoadingAccessConfig
    if ((isLoading && perfis.length === 0) || isLoadingAccessConfig) {
        return <div className="text-center"><Spinner animation="border" /> Carregando perfis...</div>;
    }

    // Função para verificar se um perfil tem acesso a um módulo ou aba específica
    const checkAccess = (perfil, moduleKey, tabKey = null) => {
        if (!perfil || !moduleKey) return false;

        // Lógica para perfis PADRÃO (baseado em roles no dynamicAccessConfig)
        if (perfil.tipo === 'padrao') {
            const moduleConfig = dynamicAccessConfig[moduleKey];
            if (!moduleConfig) return false;

            // Verifica o acesso ao módulo (header)
            const hasModuleAccess = moduleConfig.header?.roles?.includes(perfil.nome) || false;
            if (!tabKey) {
                return hasModuleAccess;
            }

            // Para ter acesso a uma aba, o perfil padrão precisa ter acesso ao módulo.
            if (!hasModuleAccess) return false;

            // Encontra a configuração da aba específica e verifica as roles dela
            const tabConfig = Object.values(moduleConfig).find(item => item && item.key === tabKey);
            return tabConfig?.roles?.includes(perfil.nome) || false;
        }
        
        // Lógica para perfis CUSTOMIZADOS (baseado no objeto 'permissoes' do perfil)
        if (perfil.tipo === 'customizado') {
            // Verifica o acesso ao módulo
            const hasModuleAccess = perfil.permissoes?.modulos?.includes(moduleKey) || false;
            if (!tabKey) {
                return hasModuleAccess;
            }

            // Para ter acesso a uma aba, o perfil customizado precisa ter acesso ao módulo.
            if (!hasModuleAccess) return false;

            // Verifica o acesso à aba
            return perfil.permissoes?.abas?.[moduleKey]?.includes(tabKey) || false;
            
        }
        return false; // Retorno padrão se o tipo de perfil não for 'padrao' ou 'customizado'
    };

    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${Math.random()}`} {...props}>
            {text}
        </Tooltip>
    );

    return (
        <>
            <Row className="mb-3 align-items-center">
                <Col>
                    <h4>Matriz de Permissões por Perfil</h4>
                </Col>
                <Col xs="auto" className="text-end">
                    <Button variant="primary" onClick={() => handleOpenModal()}>
                        Novo Perfil Customizado
                    </Button>
                </Col>
            </Row>

            <Alert variant="info" className="mb-4">
                <Alert.Heading>Entendendo os Perfis Padrão</Alert.Heading>
                <p>
                    O sistema possui perfis padrão com acessos pré-definidos para facilitar a organização da sua clínica:
                </p>
                <ul>
                    <li><strong>Representante Legal:</strong> Acesso total ao sistema, incluindo configurações da entidade e gestão de perfis.</li>
                    <li><strong>Médico:</strong> Acesso focado nas funcionalidades do consultório, como prontuários, agendamentos próprios e atendimento.</li>
                    <li><strong>Recepção:</strong> Perfil essencial para o fluxo diário.</li>
                </ul>
                <hr />
                <p className="mb-0">
                    Para que o sistema funcione em sua capacidade máxima, é ideal que exista pelo menos um colaborador com o perfil de <strong>Recepção</strong>.
                    Este perfil pode visualizar a agenda de todos os médicos, gerenciar a ordem de chegada, realizar check-in de pacientes e registrar pagamentos.
                </p>
            </Alert>

            <Table striped bordered hover responsive className="permission-matrix-table">
                <thead>
                    <tr>
                        <th style={{ minWidth: '250px' }}>Permissão (Módulo / Aba)</th>
                        {perfis.map(perfil => (
                            <th key={perfil.id} className="text-center profile-header">
                                <OverlayTrigger
                                    placement="top"
                                    delay={{ show: 250, hide: 400 }}
                                    overlay={(props) => renderTooltip(props, perfil.descricao || 'Sem descrição')}
                                >
                                    <div>{perfil.nome}</div>
                                </OverlayTrigger>
                                <small className={`d-block badge bg-${perfil.tipo === 'padrao' ? 'secondary' : 'info'}`}>
                                    {perfil.tipo === 'padrao' ? 'Padrão' : 'Customizado'}
                                </small>
                                {perfil.tipo === 'customizado' && (
                                    <div className="mt-1">
                                        <Button variant="outline-warning" size="sm" className="me-1 py-0 px-1" onClick={() => handleOpenModal(perfil)}>
                                            <small>E</small>
                                        </Button>
                                        <Button variant="outline-danger" size="sm" className="py-0 px-1" onClick={() => handleDelete(perfil.id, perfil.tipo)}>
                                            <small>X</small>
                                        </Button>
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {structuredPermissions.map(modulePerm => (
                        <React.Fragment key={modulePerm.moduleKey}>
                            <tr className="module-header-row">
                                <td>
                                    <strong>{modulePerm.moduleTitle}</strong> (Módulo)
                                </td>
                                {perfis.map(perfil => (
                                    <td key={`${modulePerm.moduleKey}-${perfil.id}`} className="text-center">
                                        {checkAccess(perfil, modulePerm.moduleKey) ? '✔️' : '❌'}
                                    </td>
                                ))}
                            </tr>
                            {modulePerm.tabs.map(tabPerm => (
                                <tr key={`${modulePerm.moduleKey}-${tabPerm.tabKey}`}>
                                    <td style={{ paddingLeft: '30px' }}>
                                        {tabPerm.tabTitle}
                                    </td>
                                    {perfis.map(perfil => (
                                        <td key={`${modulePerm.moduleKey}-${tabPerm.tabKey}-${perfil.id}`} className="text-center">
                                            {checkAccess(perfil, modulePerm.moduleKey, tabPerm.tabKey) ? '✔️' : '❌'}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </Table>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Editar Perfil' : 'Novo Perfil Customizado'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="perfilNome">
                            <Form.Label>Nome do Perfil*</Form.Label>
                            <Form.Control type="text" name="nome" value={currentPerfil.nome} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="perfilDescricao">
                            <Form.Label>Descrição</Form.Label>
                            <Form.Control as="textarea" rows={3} name="descricao" value={currentPerfil.descricao} onChange={handleChange} />
                        </Form.Group>

                        <h5>Permissões:</h5>
                            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '10px', borderRadius: '0.25rem' }}>
                                {structuredPermissions.map(modulePerm => (
                                    <div key={modulePerm.moduleKey} className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            id={`module-${modulePerm.moduleKey}`}
                                            label={<strong>{modulePerm.moduleTitle} (Módulo)</strong>}
                                            checked={currentPerfil.permissoes?.modulos?.includes(modulePerm.moduleKey) || false}
                                            onChange={(e) => handlePermissaoModuloChange(modulePerm.moduleKey, e.target.checked)}
                                        />
                                        {modulePerm.tabs.length > 0 && (
                                            <div style={{ marginLeft: '25px', marginTop: '5px' }}>
                                                {modulePerm.tabs.map(tabPerm => (
                                                    <Form.Check
                                                        key={tabPerm.tabKey}
                                                        type="checkbox"
                                                        id={`tab-${modulePerm.moduleKey}-${tabPerm.tabKey}`}
                                                        label={tabPerm.tabTitle}
                                                        checked={
                                                            (currentPerfil.permissoes?.modulos?.includes(modulePerm.moduleKey) &&
                                                            currentPerfil.permissoes?.abas?.[modulePerm.moduleKey]?.includes(tabPerm.tabKey)) || false
                                                        }
                                                        onChange={(e) => handlePermissaoAbaChange(modulePerm.moduleKey, tabPerm.tabKey, e.target.checked)}
                                                        disabled={!currentPerfil.permissoes?.modulos?.includes(modulePerm.moduleKey)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" onClick={handleCloseModal} className="me-2" disabled={isLoading}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={isLoading}>
                                {isLoading ? <Spinner as="span" animation="border" size="sm" /> : (isEditing ? 'Salvar Alterações' : 'Criar Perfil')}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default PerfisTab;